/**
 * Paleta de comandos del sistema (⌘K): scrim + cmdk (input, lista con
 * sk-active y meta, pie de atajos). Presentacional y controlada; el
 * teclado (↑↓ ↵ esc) lo maneja aquí para que toda paleta suene igual.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { cx } from "../cx";

export interface CmdkItem { glyph: ReactNode; label: string; meta: ReactNode; run: () => void }

export function Cmdk({ open, value, onChange, items, selected, onSelected, onClose, placeholder, className }: {
  open: boolean; value: string; onChange: (v: string) => void;
  items: ReadonlyArray<CmdkItem>; selected: number; onSelected: (i: number) => void;
  onClose: () => void; placeholder: string; className?: string | undefined;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);
  if (!open) return null;
  const run = (i: number) => { const it = items[i]; if (it) { onClose(); it.run(); } };
  return (
    <>
      <div className="sk-scrim sk-open" onClick={onClose} />
      <div className={cx(className, "sk-open")} role="dialog" aria-label="Paleta de comandos">
        <div className="sk-cmdk">
          <input
            ref={inputRef}
            placeholder={placeholder}
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); onSelected(Math.min(selected + 1, items.length - 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); onSelected(Math.max(selected - 1, 0)); }
              if (e.key === "Enter") run(selected);
            }}
          />
          <div className="sk-cmdk-list">
            {items.map((it, i) => (
              <button type="button" key={i} className={cx("sk-menu-item", i === selected && "sk-active")} onClick={() => run(i)}>
                <span className="sk-fainted">{it.glyph}</span>{it.label}<span className="sk-menu-meta">{it.meta}</span>
              </button>
            ))}
            {items.length === 0 && <div className="sk-listbox-empty">Nada casa con eso.</div>}
          </div>
          <div className="sk-cmdk-foot"><span>↑↓ navegar</span><span>↵ ejecutar</span><span>esc cerrar</span></div>
        </div>
      </div>
    </>
  );
}
